import React, { useEffect, useState, useCallback } from "react";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Building2, Check, X, Loader2 } from "lucide-react";

/** Approval card shown at the very top of the app (header area) when a company
 *  invited THIS user. The person only becomes a member after accepting.
 *  Refreshes live via the user WebSocket channel (osc-user-notify). */
export const CompanyInviteBanner = () => {
  const { user, setUser, t } = useApp();
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(null); // invite id being answered

  const load = useCallback(() => {
    if (!user) return;
    api.get("/company/invites/mine").then((r) => setInvites(r.data || [])).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Live: new invite / answer somewhere else -> refetch
  useEffect(() => {
    const onNotify = (e) => {
      const inner = e?.detail?.inner;
      if (inner === "company_invite" || inner === "company_invite_answer") load();
    };
    window.addEventListener("osc-user-notify", onNotify);
    return () => window.removeEventListener("osc-user-notify", onNotify);
  }, [load]);

  const respond = async (inv, action) => {
    setBusy(inv.id);
    try {
      const { data } = await api.post(`/company/invites/${inv.id}/respond`, { action });
      setInvites((list) => list.filter((i) => i.id !== inv.id));
      if (action === "accept") {
        if (data.user) setUser(data.user);
        toast.success(`${t("invite_accepted_toast")} — ${inv.company_name}`);
      } else {
        toast.success(t("invite_declined_toast"));
      }
    } catch (e) {
      toast.error(t("something_wrong"));
      load();
    } finally { setBusy(null); }
  };

  if (!user || invites.length === 0) return null;

  return (
    <div className="shrink-0 w-full" data-testid="company-invite-banner">
      {invites.map((inv) => (
        <div
          key={inv.id}
          data-testid={`company-invite-card-${inv.id}`}
          className="flex items-center gap-3 px-4 py-2.5 border-b bg-primary/5 border-primary/20"
        >
          {inv.company_logo ? (
            <img
              src={`data:image/jpeg;base64,${inv.company_logo}`}
              alt=""
              className="h-9 w-9 rounded-lg object-contain bg-white border shrink-0"
              data-testid="company-invite-logo"
            />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Building2 className="h-4.5 w-4.5 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground truncate">{t("company_invite_title")}</p>
            <p className="text-[11px] text-muted-foreground leading-snug" data-testid="company-invite-text">
              {t("company_invite_text")
                .replace("{admin}", inv.invited_by_name || "")
                .replace("{company}", inv.company_name || "")
                .replace("{role}", t(inv.role) || inv.role)}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button
              size="sm"
              className="h-8"
              data-testid={`company-invite-accept-${inv.id}`}
              disabled={busy === inv.id}
              onClick={() => respond(inv, "accept")}
            >
              {busy === inv.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />}
              {t("reg_approve")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              data-testid={`company-invite-decline-${inv.id}`}
              disabled={busy === inv.id}
              onClick={() => respond(inv, "decline")}
            >
              <X className="h-3.5 w-3.5 mr-1" /> {t("reg_reject")}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
